// src/pages/AdminDashboard.js

import React, { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getCardBalance } from '../api/CardAPI';

import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip
} from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';

import FeeManagementPanel from '../components/FeeManagementPanel';
import CardPricingPanel from '../components/CardPricingPanel';

export default function AdminDashboard() {
  const { logout } = useAuth();

  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showFeeManagement, setShowFeeManagement] = useState(false);
  const [showPricingManagement, setShowPricingManagement] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newSan, setNewSan] = useState('');
  const [newCardName, setNewCardName] = useState('');

  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);

  const [newFullName, setNewFullName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('customer');

  const [realBalances, setRealBalances] = useState({});

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editCardIndex, setEditCardIndex] = useState(-1);

  const [editSan, setEditSan] = useState('');
  const [editCardName, setEditCardName] = useState('');
  const [editMinBalance, setEditMinBalance] = useState(0);
  const [editLowBalanceNotified, setEditLowBalanceNotified] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setUsers(data);
      },
      (err) => {
        setErrorMsg('Failed to load: ' + err.message);
      }
    );

    return () => unsub();
  }, []);

  const loadBalanceForSan = async (san) => {
    if (realBalances[san] == null) {
      try {
        const bal = await getCardBalance(san);
        setRealBalances((prev) => ({ ...prev, [san]: bal }));
      } catch (err) {
        // Error logging removed for security
      }
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchText) return true;
    const t = searchText.toLowerCase();
    const nameMatch  = (u.name  || '').toLowerCase().includes(t);
    const emailMatch = (u.email || '').toLowerCase().includes(t);
    const roleMatch  = (u.role  || '').toLowerCase().includes(t);

    const cardMatch  = (u.cards || []).some((card) => {
      const s = (card.san || '').toLowerCase();
      const cName = (card.cardName || '').toLowerCase();
      if (s.includes(t) || cName.includes(t)) return true;
      if (t.length <= 4 && s.endsWith(t)) return true;
      return false;
    });

    return (nameMatch || emailMatch || roleMatch || cardMatch);
  });

  const handleAddCard = async (userId) => {
    if (!newSan.trim()) return;
    const userDocRef = doc(db, 'users', userId);
    try {
      await updateDoc(userDocRef, {
        cards: arrayUnion({
          san: newSan,
          cardName: newCardName,
          balance: 0.0,
          currency: 'USD',
          minBalance: 0.0,
          lowBalanceNotified: false
        })
      });
      setInfoMsg(`Card ${newSan} added successfully.`);
      setNewSan('');
      setNewCardName('');
      setSelectedUserId(null);
    } catch (err) {
      setErrorMsg('Failed to add card: ' + err.message);
    }
  };

  const handleRemoveCard = async (userId, card) => {
    const userDocRef = doc(db, 'users', userId);
    try {
      await updateDoc(userDocRef, {
        cards: arrayRemove(card)
      });
      setInfoMsg(`Card was removed successfully.`);
    } catch (err) {
      setErrorMsg('Failed to remove card: ' + err.message);
    }
  };

  const handleEditCardClick = (user, card, index) => {
    setEditDialogOpen(true);
    setEditUserId(user.id);
    setEditCardIndex(index);

    setEditSan(card.san || '');
    setEditCardName(card.cardName || '');
    setEditMinBalance(card.minBalance || 0);
    setEditLowBalanceNotified(card.lowBalanceNotified || false);
  };

  const handleSaveCardEdits = async () => {
    if (!editUserId || editCardIndex < 0) {
      setErrorMsg('No card selected for edit.');
      setEditDialogOpen(false);
      return;
    }
    try {
      const userDocRef = doc(db, 'users', editUserId);
      const userSnap = await getDoc(userDocRef);
      if (!userSnap.exists()) {
        setErrorMsg('User not found.');
        setEditDialogOpen(false);
        return;
      }

      const userData = userSnap.data();
      const cardsArr = userData.cards || [];
      if (editCardIndex >= cardsArr.length) {
        setErrorMsg('Card index out of range.');
        setEditDialogOpen(false);
        return;
      }

      cardsArr[editCardIndex] = {
        ...cardsArr[editCardIndex],
        san: editSan,
        cardName: editCardName,
        minBalance: parseFloat(editMinBalance),
        lowBalanceNotified: editLowBalanceNotified
      };

      await updateDoc(userDocRef, { cards: cardsArr });
      setInfoMsg('Card updated successfully.');
    } catch (err) {
      setErrorMsg('Failed to edit card: ' + err.message);
    } finally {
      setEditDialogOpen(false);
    }
  };

  const handleCreateUserDoc = async () => {
    if (!newFullName.trim() || !newEmail.trim()) {
      setErrorMsg('Name or Email is missing.');
      return;
    }
    
    // Email validation
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(newEmail)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    
    try {
      const userId = crypto.randomUUID();
      await setDoc(doc(db, 'users', userId), {
        name: newFullName,
        email: newEmail,
        role: newRole,
        cards: []
      });
      setInfoMsg(`User ${newFullName} created successfully (Firestore only).`);
      setShowCreateUserDialog(false);
      setNewFullName('');
      setNewEmail('');
      setNewRole('customer');
    } catch (err) {
      setErrorMsg('Failed to create user: ' + err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        p: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Typography variant="h4">Admin Dashboard</Typography>
        <Button
          onClick={handleLogout}
          variant="contained"
          color="secondary"
          startIcon={<LogoutIcon />}
        >
          Logout
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Collapse in={!!infoMsg}>
          <Alert
            severity="success"
            onClose={() => setInfoMsg('')}
            sx={{ backgroundColor: 'var(--accent-success)' }}
          >
            {infoMsg}
          </Alert>
        </Collapse>
        <Collapse in={!!errorMsg} sx={{ mt: 1 }}>
          <Alert
            severity="error"
            onClose={() => setErrorMsg('')}
            sx={{ backgroundColor: 'var(--accent-error)' }}
          >
            {errorMsg}
          </Alert>
        </Collapse>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'var(--bg-card)',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by name/email/card..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 100 }}
          sx={{
            input: { color: 'var(--text-primary)' },
            label: { color: 'var(--text-primary)' },
            bgcolor: 'var(--bg-secondary)'
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => setSearchText('')}
        >
          Clear
        </Button>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant={!showFeeManagement && !showPricingManagement ? "contained" : "outlined"}
          onClick={() => {
            setShowFeeManagement(false);
            setShowPricingManagement(false);
          }}
          sx={{ color: 'var(--text-primary)' }}
        >
          User Management
        </Button>
        <Button
          variant={showFeeManagement ? "contained" : "outlined"}
          onClick={() => {
            setShowFeeManagement(true);
            setShowPricingManagement(false);
          }}
          startIcon={<AttachMoneyIcon />}
          sx={{ color: 'var(--text-primary)' }}
        >
          Fee Management
        </Button>
        <Button
          variant={showPricingManagement ? "contained" : "outlined"}
          onClick={() => {
            setShowFeeManagement(false);
            setShowPricingManagement(true);
          }}
          startIcon={<AttachMoneyIcon />}
          sx={{ color: 'var(--text-primary)' }}
        >
          Card Pricing
        </Button>
        {!showFeeManagement && !showPricingManagement && (
          <Button
            variant="outlined"
            onClick={() => setShowCreateUserDialog(true)}
            startIcon={<AddIcon />}
            sx={{ ml: 'auto', color: 'var(--text-primary)', borderColor: 'var(--primary-blue)' }}
          >
            Create New User
          </Button>
        )}
      </Box>

      {showFeeManagement ? (
        <FeeManagementPanel />
      ) : showPricingManagement ? (
        <CardPricingPanel />
      ) : (
        <Box>
          {filteredUsers.map((user) => {
          const userCards = user.cards || [];

          return (
            <Paper
              key={user.id}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'var(--bg-card)',
                borderRadius: 2
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: 'var(--text-primary)' }}>
                {user.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
                {user.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Role: {user.role}
              </Typography>

              <Divider sx={{ my: 1, borderColor: 'var(--border-primary)' }} />

              {userCards.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--accent-error)' }}>
                  No cards
                </Typography>
              ) : (
                userCards.map((card, idx) => {
                  const san = card.san;
                  if (san) loadBalanceForSan(san);
                  const realBal = realBalances[san] != null
                    ? realBalances[san]
                    : card.balance || 0;

                  return (
                    <Box key={san + idx} sx={{ mb: 1 }}>
                      <Accordion sx={{ bgcolor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: 'var(--text-primary)' }} />}
                        >
                          <Typography>
                            {card.cardName || 'No Name'} (SAN: {san})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Balance: {realBal.toFixed(2)} {card.currency || 'USD'}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            MinBalance: {card.minBalance ?? 0}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Notified? {card.lowBalanceNotified ? 'Yes' : 'No'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit Card">
                              <IconButton
                                sx={{ color: 'var(--text-primary)' }}
                                onClick={() => handleEditCardClick(user, card, idx)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove Card">
                              <IconButton
                                sx={{ color: 'var(--accent-error)' }}
                                onClick={() => handleRemoveCard(user.id, card)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  );
                })
              )}

              {selectedUserId === user.id ? (
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <TextField
                    label="New SAN"
                    variant="outlined"
                    value={newSan}
                    onChange={(e) => setNewSan(e.target.value)}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      input: { color: 'var(--text-primary)' },
                      label: { color: 'var(--text-primary)' },
                      bgcolor: 'var(--bg-secondary)'
                    }}
                  />
                  <TextField
                    label="Card Name (optional)"
                    variant="outlined"
                    value={newCardName}
                    onChange={(e) => setNewCardName(e.target.value)}
                    inputProps={{ maxLength: 30 }}
                    sx={{
                      input: { color: 'var(--text-primary)' },
                      label: { color: 'var(--text-primary)' },
                      bgcolor: 'var(--bg-secondary)'
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleAddCard(user.id)}
                  >
                    Add Card
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ color: 'var(--text-primary)', borderColor: 'var(--primary-blue)' }}
                    onClick={() => setSelectedUserId(null)}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setSelectedUserId(user.id);
                      setNewSan('');
                      setNewCardName('');
                    }}
                  >
                    Add Card
                  </Button>
                </Box>
              )}
            </Paper>
          );
          })}
        </Box>
      )}

      <Dialog
        open={showCreateUserDialog}
        onClose={() => setShowCreateUserDialog(false)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-card)' } }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name"
            variant="outlined"
            value={newFullName}
            onChange={(e) => setNewFullName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 50 }}
            sx={{
              mb: 2,
              input: { color: 'var(--text-primary)' },
              label: { color: 'var(--text-primary)' },
              bgcolor: 'var(--bg-secondary)'
            }}
          />
          <TextField
            label="Email"
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 100 }}
            sx={{
              mb: 2,
              input: { color: 'var(--text-primary)' },
              label: { color: 'var(--text-primary)' },
              bgcolor: 'var(--bg-secondary)'
            }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: 'var(--text-primary)' }}>Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              label="Role"
              sx={{ color: 'var(--text-primary)', bgcolor: 'var(--bg-secondary)' }}
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateUserDialog(false)} sx={{ color: 'var(--text-primary)' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateUserDoc}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-card)', color: 'var(--text-primary)' } }}
      >
        <DialogTitle>Edit Card</DialogTitle>
        <DialogContent>
          <TextField
            label="SAN"
            variant="outlined"
            value={editSan}
            onChange={(e) => setEditSan(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 50 }}
            sx={{
              mb: 2,
              input: { color: 'var(--text-primary)' },
              label: { color: 'var(--text-primary)' },
              bgcolor: 'var(--bg-secondary)'
            }}
          />
          <TextField
            label="Card Name"
            variant="outlined"
            value={editCardName}
            onChange={(e) => setEditCardName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 30 }}
            sx={{
              mb: 2,
              input: { color: 'var(--text-primary)' },
              label: { color: 'var(--text-primary)' },
              bgcolor: 'var(--bg-secondary)'
            }}
          />
          <TextField
            label="Min Balance"
            variant="outlined"
            value={editMinBalance}
            onChange={(e) => setEditMinBalance(e.target.value)}
            fullWidth
            sx={{
              mb: 2,
              input: { color: 'var(--text-primary)' },
              label: { color: 'var(--text-primary)' },
              bgcolor: 'var(--bg-secondary)'
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>lowBalanceNotified?</Typography>
            <Switch
              checked={editLowBalanceNotified}
              onChange={(e) => setEditLowBalanceNotified(e.target.checked)}
              color="primary"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: 'var(--text-primary)' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveCardEdits}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
